import type { ILanguageResources, ITranslationModule } from '../../interfaces';
import { interpolate } from '../../global';

// Logical area: AppIndex General Strings
class Id {
  private constructor() {}

  public static readonly prefix = 'UI.webmodule-common.other.AppIndex.';

  public static readonly registerNew2FAApp = `${Id.prefix}registerNew2FAApp`;
  public static readonly enable2FA = `${Id.prefix}enable2FA`;
  public static readonly user = `${Id.prefix}user`;
  public static readonly logout = `${Id.prefix}logout`;
  public static readonly build = `${Id.prefix}build`;
  public static readonly welcome = `${Id.prefix}welcome`;
  public static readonly language = `${Id.prefix}language`;
  public static readonly setAsDefault = `${Id.prefix}setAsDefault`;
  public static readonly changeLanguage = `${Id.prefix}changeLanguage`;
  public static readonly devEnvironment = `${Id.prefix}devEnvironment`;
  public static readonly testEnvironment = `${Id.prefix}testEnvironment`;
  public static readonly preferences = `${Id.prefix}preferences`;
  public static readonly preferencesTitle = `${Id.prefix}preferencesTitle`;
  public static readonly prefRowsPerPage = `${Id.prefix}prefRowsPerPage`;
  public static readonly prefTabPosition = `${Id.prefix}prefTabPosition`;
  public static readonly prefTabPositionTop = `${Id.prefix}prefTabPositionTop`;
  public static readonly prefTabPositionSide = `${Id.prefix}prefTabPositionSide`;
  public static readonly prefTabStyle = `${Id.prefix}prefTabStyle`;
  public static readonly prefTabStyleClassic = `${Id.prefix}prefTabStyleClassic`;
  public static readonly prefTabStyleChip = `${Id.prefix}prefTabStyleChip`;
  public static readonly prefSectionTable = `${Id.prefix}prefSectionTable`;
  public static readonly prefSectionTabs = `${Id.prefix}prefSectionTabs`;
  public static readonly prefTableElevated = `${Id.prefix}prefTableElevated`;
  public static readonly prefTableCellBorders = `${Id.prefix}prefTableCellBorders`;
  public static readonly prefTableUppercaseHeader = `${Id.prefix}prefTableUppercaseHeader`;
  public static readonly prefStoredLocally = `${Id.prefix}prefStoredLocally`;
  public static readonly prefResetToDefaults = `${Id.prefix}prefResetToDefaults`;
}

export class LangAppIndex {
  private constructor() {}

  public static registerNew2FAApp(): string {
    return interpolate(Id.registerNew2FAApp);
  }

  public static enable2FA(): string {
    return interpolate(Id.enable2FA);
  }

  public static user(): string {
    return interpolate(Id.user);
  }

  public static logout(): string {
    return interpolate(Id.logout);
  }

  public static build(buildNumber: string): string {
    return interpolate(Id.build, { buildNumber });
  }

  public static welcome(user: string): string {
    return interpolate(Id.welcome, { user });
  }

  public static language(): string {
    return interpolate(Id.language);
  }

  public static setAsDefault(language: string): string {
    return interpolate(Id.setAsDefault, { language: language });
  }

  static changeLanguage(language: string) {
    return interpolate(Id.changeLanguage, { language: language });
  }

  public static devEnvironment(): string {
    return interpolate(Id.devEnvironment);
  }

  public static testEnvironment(): string {
    return interpolate(Id.testEnvironment);
  }

  public static preferences(): string {
    return interpolate(Id.preferences);
  }

  public static preferencesTitle(): string {
    return interpolate(Id.preferencesTitle);
  }

  public static prefRowsPerPage(): string {
    return interpolate(Id.prefRowsPerPage);
  }

  public static prefTabPosition(): string {
    return interpolate(Id.prefTabPosition);
  }

  public static prefTabPositionTop(): string {
    return interpolate(Id.prefTabPositionTop);
  }

  public static prefTabPositionSide(): string {
    return interpolate(Id.prefTabPositionSide);
  }

  public static prefTabStyle(): string {
    return interpolate(Id.prefTabStyle);
  }

  public static prefTabStyleClassic(): string {
    return interpolate(Id.prefTabStyleClassic);
  }

  public static prefTabStyleChip(): string {
    return interpolate(Id.prefTabStyleChip);
  }

  public static prefSectionTable(): string {
    return interpolate(Id.prefSectionTable);
  }

  public static prefSectionTabs(): string {
    return interpolate(Id.prefSectionTabs);
  }

  public static prefTableElevated(): string {
    return interpolate(Id.prefTableElevated);
  }

  public static prefTableCellBorders(): string {
    return interpolate(Id.prefTableCellBorders);
  }

  public static prefTableUppercaseHeader(): string {
    return interpolate(Id.prefTableUppercaseHeader);
  }

  public static prefStoredLocally(): string {
    return interpolate(Id.prefStoredLocally);
  }

  public static prefResetToDefaults(): string {
    return interpolate(Id.prefResetToDefaults);
  }
}

export class AppIndexModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // General
    register.add(Id.registerNew2FAApp, 'Register new 2FA App', 'Menu item for registering a new 2FA app');
    register.add(Id.enable2FA, 'Enable 2FA', 'Menu item for enabling 2FA');
    register.add(Id.user, 'User', 'Menu label for user');
    register.add(Id.logout, 'Logout', 'Menu item for logging out');
    register.add(Id.build, 'Build {{buildNumber}}', 'Build number display in footer');
    register.add(Id.welcome, 'Welcome {{user}}', 'Welcome message in user menu');
    register.add(Id.language, 'Language', 'Menu item for language selection');
    register.add(Id.setAsDefault, 'Set "{{language}}" as Default', 'Menu item to set language as default');
    register.add(
      Id.changeLanguage,
      'Changing language to "{{language}}" requires a page reload which may loose any current unsaved changes',
      'Confirm changing to a new display language which may loose unsaved changes'
    );
    register.add(Id.devEnvironment, 'DEV ENVIRONMENT', 'Footer label for dev environment');
    register.add(Id.testEnvironment, 'TEST ENVIRONMENT', 'Footer label for test environment');
    // Preferences
    register.add(Id.preferences, 'Preferences', 'Menu item that opens the user preferences dialog');
    register.add(Id.preferencesTitle, 'My Preferences', 'Title of the user preferences dialog');
    register.add(Id.prefRowsPerPage, 'Rows per page', 'Preference: default rows per page for tables');
    register.add(Id.prefTabPosition, 'Tab position', 'Preference: where the tab strip renders on tabbed pages');
    register.add(Id.prefTabPositionTop, 'Top', 'Tab position option: tabs across the top');
    register.add(Id.prefTabPositionSide, 'Side', 'Tab position option: tabs down the side');
    register.add(Id.prefTabStyle, 'Tab style', 'Preference: visual style of the tabs');
    register.add(Id.prefTabStyleClassic, 'Classic', 'Tab style option: filled folder tabs');
    register.add(Id.prefTabStyleChip, 'Chip', 'Tab style option: outlined chips with a sliding pill');
    register.add(Id.prefSectionTable, 'Table', 'Preference section heading grouping all table settings');
    register.add(Id.prefSectionTabs, 'Tabs', 'Preference section heading grouping all tab settings');
    register.add(
      Id.prefTableElevated,
      'Raised (drop shadow)',
      'Preference: draw tables as a raised card with a drop shadow'
    );
    register.add(Id.prefTableCellBorders, 'Cell borders', 'Preference: draw vertical borders between table cells');
    register.add(Id.prefTableUppercaseHeader, 'Uppercase headers', 'Preference: uppercase the table header labels');
    register.add(
      Id.prefStoredLocally,
      'Preferences are saved for your login on this browser and apply immediately.',
      'Note in the preferences dialog about where the settings are stored'
    );
    register.add(Id.prefResetToDefaults, 'Reset to defaults', 'Button restoring the default preferences');
  }
}
